import { getAdminData } from "./api";

/**
 * Client-side permission gating for the Question Bank module.
 *
 * These checks hide or disable UI only - they are a usability layer, never a
 * security boundary. The backend must enforce the same permission names on
 * every endpoint in api.js.
 */

export const PERMISSIONS = {
  BANK_VIEW: "question_bank.view",
  BANK_CREATE: "question_bank.create",
  BANK_UPDATE: "question_bank.update",
  BANK_DELETE: "question_bank.delete",

  UNIT_CREATE: "question_unit.create",
  UNIT_UPDATE: "question_unit.update",
  UNIT_DELETE: "question_unit.delete",

  LECTURE_CREATE: "question_lecture.create",
  LECTURE_UPDATE: "question_lecture.update",
  LECTURE_DELETE: "question_lecture.delete",

  QUESTION_CREATE: "question.create",
  QUESTION_UPDATE: "question.update",
  QUESTION_DELETE: "question.delete",
  QUESTION_IMPORT: "question.import",

  ASSIGN: "course_question_bank.assign",
  ASSIGN_REMOVE: "course_question_bank.remove",
};

export const ALL_PERMISSIONS = Object.values(PERMISSIONS);

const normalise = (raw) => {
  if (Array.isArray(raw)) return raw.map(String);
  if (typeof raw === "string") {
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) return parsed.map(String);
    } catch (e) {
      /* fall through to comma-separated form */
    }
    return raw.split(",").map((p) => p.trim()).filter(Boolean);
  }
  if (raw && typeof raw === "object") {
    return Object.keys(raw).filter((k) => !!raw[k]);
  }
  return null;
};

/**
 * Returns the current admin's permission list, or null when the backend does
 * not supply one. A null list means "not scoped" - see `can` below.
 */
export const currentPermissions = () => {
  const admin = getAdminData();
  return normalise(admin?.permissions ?? admin?.admin_permissions);
};

export const isSuperAdmin = () => {
  const admin = getAdminData();
  return (
    admin?.is_super_admin === true ||
    String(admin?.is_super_admin) === "1" ||
    String(admin?.admin_type).toLowerCase() === "super_admin"
  );
};

/**
 * Permission check.
 *
 * If the backend has not yet started returning a permission list, every check
 * passes - otherwise rolling this module out would lock existing admins out of
 * a panel that has no permission plumbing today. Once `permissions` is present
 * on the login payload, gating becomes real automatically.
 */
export const can = (permission) => {
  if (isSuperAdmin()) return true;
  const perms = currentPermissions();
  if (perms === null) return true;
  return perms.includes(permission);
};

export const canAny = (...permissions) => permissions.some((p) => can(p));
